import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Insurance & takaful",
  title: "Insurance claims agent",
  cardSummary:
    "Match a claim against the policy, the report and the receipts — and flag what doesn't add up.",
  heroSubhead:
    "We design, build and deploy an AI agent that reads a claim end to end — the policy, the incident report and every receipt — reconciles them against each other, and hands your adjuster a decision-ready file with the leakage and fraud signals flagged, while the pay-out decision stays with a human.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "Every over-payment it catches and every clean claim it fast-tracks is money — claims leakage typically runs 5–10% of paid claims, and consistent scrutiny is what a BNM conduct review looks for.",
    complexityLabel: "High",
    complexityWhy:
      "An orchestrator decomposing the claim into parallel checks, vision/OCR on reports and receipts, policy-clause reasoning, fraud signals, and BNM/MAS/PIDM/PDPA/PDPC-grade controls and audit trail.",
    timeToLive: "~a few weeks (one claim type)",
    bestFit: "Insurers & takaful operators triaging document-heavy claims",
  },
  outcomes: [
    "Stop paying what the policy doesn't cover — leakage and over-billing caught before pay-out",
    "Straight-through low-risk claims — clean files settled in minutes, not days",
    "Every claim gets the same scrutiny — no more adjuster-to-adjuster drift",
    "Fraud signals surfaced early, with the exact document and figure behind each one",
  ],
  impact: {
    manualLabel: "Manual triage today",
    manualValue: "~30–45 min",
    agentLabel: "With the agent",
    agentValue: "~6–10 min",
    saved:
      "≈ 25–35 minutes saved per claim — about 165–230 hours a month at 400 claims, roughly a full-time adjuster. And on leakage: on a portfolio paying RM 2m/month, catching even 2 points of a 5–10% leakage rate is ~RM 40,000/month recovered — the pay-back on the build is measured in weeks, not years.",
    note: "Indicative; actual time and recovery depend on claim type, document count and your current leakage rate.",
  },
  businessProblem:
    "An insurer or takaful operator in the region receives each motor, medical or property claim as a pile — the claim form, the policy schedule, an incident or police report, workshop or hospital invoices, and photos. Most of the adjuster's day goes to the mechanical part: checking that the claim is actually covered, that the amounts match the receipts, and that nothing is padded or duplicated — not the judgement call. It is slow, it drifts as fatigue sets in, two adjusters apply different scrutiny to the same file, and the padding that slips through becomes claims leakage the business quietly eats.",
  manualWorkflow: {
    steps: [
      "Open the claim file and each document",
      "Read the policy schedule to see what's actually covered",
      "Read the incident report and the receipts by eye",
      "Cross-check amounts, dates and cover against each other",
      "Chase missing documents or duplicate line items",
      "Only then make the settle / query / investigate call",
    ],
    breaks:
      "The bottleneck is reconciling the claim against the policy and the receipts — not the judgement. It's exactly the part that fatigues, varies between adjusters, and lets padding slip through as leakage.",
  },
  agenticWorkflow: {
    narrative:
      "We build it as an orchestrator-workers workflow: a lead model reads the claim, decomposes it into the checks this claim actually needs, and delegates each to a specialised worker — policy cover, receipt totals, report consistency, duplicate/fraud signals — then assembles one evidenced summary and escalates anything it can't clear to a human.",
    steps: [
      "Read the claim, policy, report & receipts",
      "Decompose into the checks this claim needs",
      "Run workers in parallel (cover · amounts · fraud)",
      "Reconcile findings & flag with evidence",
      "Assemble a decision-ready file",
    ],
    inLabel: "A claim arrives",
    outLabel: "A decision-ready claim file",
    loopAround: {
      index: 1,
      label: "The orchestrator spins up only the workers this claim needs and gathers their findings — the orchestrator-workers pattern.",
    },
    humanStep: "The adjuster makes the settle / query / investigate call",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "In the claim file",
    inMessage:
      "Motor claim, policy covers own-damage up to RM 15,000. Workshop invoice total: RM 8,400 — incl. front bumper replacement RM 1,200 (line 4) and front bumper replacement RM 1,200 (line 9). Incident report date: 12 Mar. Invoice date: 8 Mar.",
    outLabel: "What the agent flags",
    outLines: [
      "Claim within own-damage cover (RM 8,400 vs RM 15,000 limit)",
      "Duplicate line item: front bumper replacement billed twice (line 4 & line 9)",
      "Timeline inconsistency: invoice dated 8 Mar, 4 days before the 12 Mar incident",
    ],
    outResult:
      "2 issues flagged: RM 1,200 duplicate charge + repair invoiced before the incident date",
    note: "Every flag cites the exact document, line and figure · your adjuster makes the pay-out call · ~RM 0.80–1.60 per claim.",
  },
  workflowDetail: [
    {
      title: "Read the claim file",
      detail:
        "The agent ingests every document in the file — claim form, policy schedule, incident or police report, workshop or hospital invoices, and photos — reading text and, for scans and phone photos, using vision to extract the content.",
    },
    {
      title: "Decompose the claim (the orchestrator)",
      detail:
        "A lead model works out which checks this specific claim needs — a motor own-damage claim, a medical panel claim and a property claim don't need the same tests — and plans the workers to run.",
    },
    {
      title: "Run the workers",
      detail:
        "Specialised workers run in parallel: a policy-cover worker (is this peril, item and amount actually covered, within limits and sub-limits?), an amounts worker (do the receipt totals add up and match the claimed sum?), a consistency worker (do dates, parties and the report line up?), and a fraud-signal worker (duplicates, padding, edited documents, prior-claim overlap).",
    },
    {
      title: "Reconcile and flag",
      detail:
        "The orchestrator gathers the workers' findings, reconciles them, and flags the two or three things that matter — an uncovered item, a duplicated line, a mismatched date — each with the figure and the document it came from.",
    },
    {
      title: "Assemble a decision-ready file",
      detail:
        "It produces a one-page summary: the claim, the checks that passed, the recommended action (settle / query / investigate), and every flag with its evidence — ready for the adjuster to act on.",
    },
    {
      title: "Hand to a human",
      detail:
        "The adjuster reviews the flagged items and makes the settle, query or investigate decision. Nothing is auto-paid.",
    },
  ],
  architecture: [
    "Pattern: an orchestrator-workers workflow on the Claude Agent SDK — the lead model decomposes the claim at runtime and delegates to workers, each tool exposed via an in-process MCP server.",
    "Context: the policy schedule, cover rules and the claim bundle are pulled into the window — text plus vision for scanned reports and receipt photos — with the live policy data (limits, sub-limits, exclusions) read fresh per claim, not from a stale copy, and long files compacted so nothing is silently dropped.",
    "Tools: read-only lookups (policy/cover reader, a claims-history reader for prior-claim overlap, a receipt/OCR extractor, a workshop/panel-rate reference) are kept separate from any write — the agent writes only a structured recommendation into the claims queue, never a pay-out.",
    "Guardrails: no autonomous money movement — it can recommend but never settle; schema-locked output; least-privilege with no write access to the policy admin or payment system; low-confidence or high-value claims route to a senior queue.",
    "Untrusted input: every document — invoice PDFs, report text, receipt images — is treated as untrusted and prompt-injection resistant, so a doctored invoice can't steer the recommendation; PII (IC, medical, vehicle, bank details) is handled PDPA/PDPC-aligned.",
    "Recovery: where a worker's confidence is low or a document is missing, the orchestrator re-runs that worker or requests the piece, then escalates to the adjuster — it never guesses a settlement; every path ends at a human.",
    "Observability & audit: every tool call, worker finding and the final recommendation are traced and logged, and the adjuster's decision is captured against the file — a machine-readable audit trail a BNM/MAS/PIDM review can reconstruct, not a screenshot.",
    "Eval harness: a golden set of historical claims including known leakage and fraud, with deterministic checks (cover limits, arithmetic, dates) plus an LLM-as-judge on the fuzzy fraud calls, regression-tested on every change before it touches live claims.",
  ],
  models: [
    {
      task: "Read & extract from documents (incl. scans & photos)",
      model: "Claude Sonnet",
      why: "Reliable vision and structured extraction on messy invoices, reports and receipt photos.",
    },
    {
      task: "Orchestrate & reconcile across the claim",
      model: "Claude Sonnet",
      why: "The workhorse — decomposes the claim, runs the workers and reconciles findings at moderate cost.",
    },
    {
      task: "High-volume simple checks (duplicates, arithmetic, date sanity)",
      model: "Claude Haiku",
      why: "Cheapest and fastest for the bulk yes/no worker checks that don't need full reasoning.",
    },
    {
      task: "Adjudicate ambiguous fraud / borderline cover",
      model: "Claude Opus",
      why: "Invoked only when a worker flags low confidence on a hard call — a small fraction of claims.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per claim",
        value: "~RM 0.80–1.60",
        note: "input documents + orchestration + parallel workers + output summary, at Claude Sonnet token rates",
      },
      {
        label: "AI usage — at 400 claims/month",
        value: "~RM 280–560 / month",
        note: "roughly half with the Batch API for overnight, non-urgent triage runs",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "a focused single-claim-type agent is a few weeks of engineering; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to the adjuster time it removes and the leakage it recovers",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on document sizes, claim type and monthly volume. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  howWeBuild:
    "It's wired straight into your policy admin and claims system through connected tools, and it reads your live cover rules — limits, sub-limits and exclusions — per claim, so it checks against the policy as it actually stands, not a cached copy. It can recommend but never settles or moves money, and every flag it raises traces back to the exact document, line and figure it came from — a full audit trail built in from day one for a BNM/MAS/PIDM review.",
  agentLoop: [
    "the claim bundle, policy cover & claims history",
    "decompose & run workers — cover, amounts, fraud (MCP tools)",
    "reconcile the workers' findings before surfacing",
  ],
  integrations: [
    "Policy admin / claims management system",
    "Document store or claims intake inbox",
    "Optional: workshop / medical-panel rate references, anti-fraud / prior-claim lookups",
  ],
  modelChoice: {
    approach: "Single orchestrator + tiered workers",
    rationale:
      "A claim genuinely splits into independent checks — cover, amounts, consistency, fraud — so an orchestrator delegating to workers fits, but the workers are model calls within one process sharing the claim context, not separate autonomous agents — reliable, auditable, and far cheaper.",
    costNote:
      "We keep the workers as tiered in-process calls (Haiku for simple checks, Sonnet to reconcile, Opus only for hard adjudication) rather than a full multi-agent fleet (≈15× the tokens) — reserved for genuinely open-ended, breadth-first work, which claim triage is not.",
  },
  evaluation: [
    "Deterministic checks first — cover limits and sub-limits, receipt arithmetic, date and party consistency",
    "Self-verification — the orchestrator reconciles and re-checks its workers' flags before surfacing them",
    "Business KPIs — adjuster time per claim, leakage caught, straight-through rate, consistency, and false-flag rate",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure the current manual triage on a sample — time per claim, how consistently two adjusters reach the same call, how much leakage slips through, and how many clean claims are needlessly delayed. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it reach the right recommendation?",
        metrics: [
          {
            name: "Agreement with a senior adjuster",
            measures: "Its summary, flags and recommended action match an expert's on held-out claims",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Did it run the right checks for this claim?",
        metrics: [
          {
            name: "Required workers dispatched",
            measures: "Every check the claim type mandates — cover, amounts, consistency, fraud — is run",
            target: "100%",
          },
          {
            name: "No wasteful workers",
            measures: "It runs the checks the claim needs, not needless ones",
            target: "monitored",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual checks correct?",
        metrics: [
          {
            name: "Field & amount extraction accuracy",
            measures: "Did it read each figure — totals, limits, dates — correctly off the documents?",
            target: "≥ 98%",
          },
          {
            name: "Leakage / fraud recall (catch rate)",
            measures: "Of the real over-payments and padding in the golden set, how many it flags",
            target: "≥ 95%",
          },
          {
            name: "False-flag rate",
            measures: "How often it flags a clean claim as a problem",
            target: "≤ 10%",
          },
          {
            name: "No hallucinated figures",
            measures: "Every number in the summary traces to a source document",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent does not touch a live claim until it beats the human baseline and clears these GPA targets on a golden set of historical claims — including known leakage and fraud. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real historical claims — including the padded and fraudulent ones — and prove it catches leakage without over-flagging clean files before it touches live claims. A focused single-claim-type rollout is a matter of weeks.",
  ctaTitle: "Is your claims team reconciling files by hand?",
  ctaDescription:
    "Tell us your claim type and volumes and we'll tell you honestly whether an insurance claims agent is worth building — and what it would recover.",
  faqs: [
    {
      question: "Does the agent approve or pay claims?",
      answer:
        "No. It reads, reconciles and flags, and recommends an action; a human adjuster makes the settle, query or investigate decision. It never moves money. Human-in-the-loop is essential for regulated insurance and takaful in the region.",
    },
    {
      question: "How does it catch fraud and leakage?",
      answer:
        "A fraud-signal worker looks for duplicated line items, padding above panel/workshop rates, edited documents, timeline inconsistencies and overlap with prior claims — each surfaced with the exact document and figure. It flags for a human to investigate; it does not accuse.",
    },
    {
      question: "How is it kept compliant with BNM/MAS, PIDM and PDPA/PDPC?",
      answer:
        "With a full audit trail so every recommendation can be reconstructed, and built around PDPA/PDPC requirements for where claim and medical data is processed, stored and accessed. Treat this as a starting point for your compliance team, not legal advice.",
    },
    {
      question: "How long does it take to deploy?",
      answer:
        "A proof-of-concept for one focused claim type is usually a matter of weeks; a full rollout takes longer, mostly because of integration and getting adjusters comfortable, not the AI.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Insurans & takaful",
  title: "Ejen tuntutan insurans",
  cardSummary:
    "Padankan tuntutan dengan polisi, laporan dan resit — dan tandakan apa yang tidak masuk akal.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI yang membaca tuntutan dari mula hingga akhir — polisi, laporan kejadian dan setiap resit — menyelaraskannya antara satu sama lain, dan menyediakan fail sedia-keputusan untuk penilai anda dengan isyarat kebocoran dan penipuan ditandakan, sementara keputusan bayaran kekal dengan manusia.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Setiap lebihan bayaran yang ditangkap dan setiap tuntutan bersih yang dipercepat ialah wang — kebocoran tuntutan biasanya 5–10% daripada tuntutan dibayar, dan penelitian konsisten ialah yang dicari oleh semakan tatakelakuan BNM.",
    complexityLabel: "Tinggi",
    complexityWhy:
      "Orkestrator memecahkan tuntutan kepada semakan selari, visi/OCR pada laporan dan resit, penaakulan klausa polisi, isyarat penipuan, dan kawalan serta jejak audit bertaraf BNM/MAS/PIDM/PDPA/PDPC.",
    timeToLive: "~beberapa minggu (satu jenis tuntutan)",
    bestFit: "Penginsurans & pengendali takaful yang menyaring tuntutan berat dokumen",
  },
  outcomes: [
    "Berhenti membayar apa yang polisi tidak lindungi — kebocoran dan bil berlebihan ditangkap sebelum bayaran",
    "Tuntutan risiko rendah terus lepas — fail bersih diselesaikan dalam minit, bukan hari",
    "Setiap tuntutan mendapat penelitian yang sama — tiada lagi perbezaan antara penilai",
    "Isyarat penipuan didedahkan awal, dengan dokumen dan angka tepat di sebalik setiapnya",
  ],
  impact: {
    manualLabel: "Saringan manual hari ini",
    manualValue: "~30–45 min",
    agentLabel: "Dengan ejen",
    agentValue: "~6–10 min",
    saved:
      "≈ 25–35 minit dijimatkan setiap tuntutan — kira-kira 165–230 jam sebulan pada 400 tuntutan, lebih kurang seorang penilai sepenuh masa. Dan pada kebocoran: pada portfolio yang membayar RM 2j/bulan, menangkap 2 mata sahaja daripada kadar kebocoran 5–10% ialah ~RM 40,000/bulan dipulihkan — pulangan atas pembinaan diukur dalam minggu, bukan tahun.",
    note: "Anggaran; masa dan pemulihan sebenar bergantung pada jenis tuntutan, bilangan dokumen dan kadar kebocoran semasa anda.",
  },
  businessProblem:
    "Penginsurans atau pengendali takaful di rantau ini menerima setiap tuntutan motor, perubatan atau harta sebagai satu longgokan — borang tuntutan, jadual polisi, laporan kejadian atau polis, invois bengkel atau hospital, dan foto. Sebahagian besar hari penilai dihabiskan untuk bahagian mekanikal: memastikan tuntutan benar-benar dilindungi, jumlah sepadan dengan resit, dan tiada yang ditokok atau diduakan — bukan penilaian. Ia perlahan, hilang ketepatan apabila letih, dua penilai menerapkan penelitian berbeza pada fail yang sama, dan tokokan yang terlepas menjadi kebocoran tuntutan yang perniagaan tanggung secara senyap.",
  manualWorkflow: {
    steps: [
      "Buka fail tuntutan dan setiap dokumen",
      "Baca jadual polisi untuk lihat apa yang benar-benar dilindungi",
      "Baca laporan kejadian dan resit secara manual",
      "Semak silang jumlah, tarikh dan perlindungan antara satu sama lain",
      "Kejar dokumen yang hilang atau item baris pendua",
      "Barulah buat keputusan selesai / soal / siasat",
    ],
    breaks:
      "Halangan ialah menyelaras tuntutan dengan polisi dan resit — bukan penilaian. Itulah bahagian yang meletihkan, berbeza antara penilai, dan membiarkan tokokan terlepas sebagai kebocoran.",
  },
  agenticWorkflow: {
    narrative:
      "Kami membinanya sebagai aliran orchestrator-workers: model utama membaca tuntutan, memecahkannya kepada semakan yang tuntutan ini benar-benar perlukan, dan mengagihkan setiapnya kepada pekerja khusus — perlindungan polisi, jumlah resit, ketekalan laporan, isyarat pendua/penipuan — kemudian menghimpun satu ringkasan berbukti dan mengangkat apa-apa yang tidak dapat diselesaikan kepada manusia.",
    steps: [
      "Baca tuntutan, polisi, laporan & resit",
      "Pecahkan kepada semakan yang tuntutan ini perlukan",
      "Jalankan pekerja secara selari (perlindungan · jumlah · penipuan)",
      "Selaraskan penemuan & tandakan dengan bukti",
      "Himpun fail sedia-keputusan",
    ],
    inLabel: "Tuntutan tiba",
    outLabel: "Fail tuntutan sedia-keputusan",
    loopAround: {
      index: 1,
      label: "Orkestrator hanya menghidupkan pekerja yang tuntutan ini perlukan dan mengumpul penemuan mereka — corak orchestrator-workers.",
    },
    humanStep: "Penilai membuat keputusan selesai / soal / siasat",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Dalam fail tuntutan",
    inMessage:
      "Tuntutan motor, polisi lindung kerosakan-sendiri sehingga RM 15,000. Jumlah invois bengkel: RM 8,400 — termasuk gantian bampar depan RM 1,200 (baris 4) dan gantian bampar depan RM 1,200 (baris 9). Tarikh laporan kejadian: 12 Mac. Tarikh invois: 8 Mac.",
    outLabel: "Apa yang ejen tandakan",
    outLines: [
      "Tuntutan dalam perlindungan kerosakan-sendiri (RM 8,400 vs had RM 15,000)",
      "Item baris pendua: gantian bampar depan dibil dua kali (baris 4 & baris 9)",
      "Ketaktekalan garis masa: invois bertarikh 8 Mac, 4 hari sebelum kejadian 12 Mac",
    ],
    outResult:
      "2 isu ditandakan: caj pendua RM 1,200 + pembaikan diinvois sebelum tarikh kejadian",
    note: "Setiap tanda memetik dokumen, baris dan angka tepat · penilai anda membuat keputusan bayaran · ~RM 0.80–1.60 setiap tuntutan.",
  },
  workflowDetail: [
    {
      title: "Baca fail tuntutan",
      detail:
        "Ejen membaca setiap dokumen dalam fail — borang tuntutan, jadual polisi, laporan kejadian atau polis, invois bengkel atau hospital, dan foto — membaca teks dan, untuk imbasan serta foto telefon, menggunakan visi untuk mengekstrak kandungan.",
    },
    {
      title: "Pecahkan tuntutan (orkestrator)",
      detail:
        "Model utama memikirkan semakan yang tuntutan khusus ini perlukan — tuntutan kerosakan-sendiri motor, tuntutan panel perubatan dan tuntutan harta tidak memerlukan ujian yang sama — dan merancang pekerja untuk dijalankan.",
    },
    {
      title: "Jalankan pekerja",
      detail:
        "Pekerja khusus berjalan secara selari: pekerja perlindungan-polisi (adakah bahaya, item dan jumlah ini benar-benar dilindungi, dalam had dan sub-had?), pekerja jumlah (adakah jumlah resit betul dan sepadan dengan jumlah dituntut?), pekerja ketekalan (adakah tarikh, pihak dan laporan sepadan?), dan pekerja isyarat-penipuan (pendua, tokokan, dokumen disunting, pertindihan tuntutan terdahulu).",
    },
    {
      title: "Selaras dan tanda",
      detail:
        "Orkestrator mengumpul penemuan pekerja, menyelaraskannya, dan menandakan dua atau tiga perkara yang penting — item tidak dilindungi, baris pendua, tarikh tak sepadan — setiap satu dengan angka dan dokumen sumbernya.",
    },
    {
      title: "Himpun fail sedia-keputusan",
      detail:
        "Ia menghasilkan ringkasan satu halaman: tuntutan, semakan yang lulus, tindakan disyorkan (selesai / soal / siasat), dan setiap tanda dengan buktinya — sedia untuk penilai bertindak.",
    },
    {
      title: "Serah kepada manusia",
      detail:
        "Penilai menyemak perkara yang ditandakan dan membuat keputusan selesai, soal atau siasat. Tiada apa-apa dibayar secara automatik.",
    },
  ],
  architecture: [
    "Corak: aliran orchestrator-workers atas Claude Agent SDK — model utama memecahkan tuntutan pada masa larian dan mengagihkan kepada pekerja, setiap alat didedahkan melalui pelayan MCP dalam-proses.",
    "Konteks: jadual polisi, peraturan perlindungan dan himpunan tuntutan ditarik ke tetingkap — teks serta visi untuk laporan diimbas dan foto resit — dengan data polisi langsung (had, sub-had, pengecualian) dibaca segar setiap tuntutan, bukan salinan lapuk, dan fail panjang dimampatkan supaya tiada yang tercicir secara senyap.",
    "Alat: semakan baca-sahaja (pembaca polisi/perlindungan, pembaca sejarah-tuntutan untuk pertindihan terdahulu, pengekstrak resit/OCR, rujukan kadar bengkel/panel) diasingkan daripada sebarang tulisan — ejen hanya menulis cadangan berstruktur ke baris gilir tuntutan, tidak pernah bayaran.",
    "Pagar keselamatan: tiada pergerakan wang autonomi — ia boleh mencadang tetapi tidak pernah menyelesaikan; output terkunci-skema; keistimewaan-minimum tanpa akses tulis ke sistem pentadbiran polisi atau bayaran; tuntutan keyakinan-rendah atau bernilai tinggi dihalakan ke baris gilir kanan.",
    "Input tidak dipercayai: setiap dokumen — PDF invois, teks laporan, imej resit — dianggap tidak dipercayai dan tahan suntikan-prompt, jadi invois diusik tidak boleh mengemudi cadangan; PII (IC, perubatan, kenderaan, butiran bank) dikendalikan selaras PDPA/PDPC.",
    "Pemulihan: apabila keyakinan pekerja rendah atau dokumen hilang, orkestrator menjalankan semula pekerja itu atau meminta bahagian itu, kemudian menyerahkan kepada penilai — ia tidak pernah meneka penyelesaian; setiap laluan berakhir pada manusia.",
    "Kebolehlihatan & audit: setiap panggilan alat, penemuan pekerja dan cadangan akhir dijejak dan dilog, dan keputusan penilai direkod terhadap fail — jejak audit boleh-dibaca-mesin yang semakan BNM/MAS/PIDM boleh bina semula, bukan tangkap skrin.",
    "Rangka penilaian: set emas tuntutan sejarah termasuk kebocoran dan penipuan diketahui, dengan semakan deterministik (had perlindungan, aritmetik, tarikh) serta LLM-sebagai-hakim untuk panggilan penipuan kabur, diuji regresi pada setiap perubahan sebelum menyentuh tuntutan langsung.",
  ],
  models: [
    {
      task: "Baca & ekstrak daripada dokumen (termasuk imbasan & foto)",
      model: "Claude Sonnet",
      why: "Visi yang boleh dipercayai dan ekstrak berstruktur pada invois, laporan dan foto resit yang berselerak.",
    },
    {
      task: "Orkestra & selaras merentas tuntutan",
      model: "Claude Sonnet",
      why: "Kuda kerja — memecahkan tuntutan, menjalankan pekerja dan menyelaraskan penemuan pada kos sederhana.",
    },
    {
      task: "Semakan mudah bervolum tinggi (pendua, aritmetik, kewarasan tarikh)",
      model: "Claude Haiku",
      why: "Paling murah dan pantas untuk semakan pekerja ya/tidak pukal yang tidak memerlukan penaakulan penuh.",
    },
    {
      task: "Adili penipuan samar / perlindungan sempadan",
      model: "Claude Opus",
      why: "Digunakan hanya apabila pekerja menandakan keyakinan rendah pada panggilan sukar — sebahagian kecil tuntutan.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap tuntutan",
        value: "~RM 0.80–1.60",
        note: "dokumen input + orkestrasi + pekerja selari + ringkasan output, pada kadar token Claude Sonnet",
      },
      {
        label: "Penggunaan AI — pada 400 tuntutan/bulan",
        value: "~RM 280–560 / bulan",
        note: "lebih kurang separuh dengan Batch API untuk larian saringan bukan segera pada waktu malam",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "ejen satu jenis tuntutan yang fokus adalah beberapa minggu kejuruteraan; kami beri sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding masa penilai yang dihapuskan dan kebocoran yang dipulihkan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada saiz dokumen, jenis tuntutan dan volum bulanan. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  howWeBuild:
    "Ia disambung terus ke pentadbiran polisi dan sistem tuntutan anda melalui alat yang disambung, dan membaca peraturan perlindungan langsung anda — had, sub-had dan pengecualian — setiap tuntutan, jadi ia menyemak terhadap polisi seperti keadaan sebenarnya, bukan salinan cache. Ia boleh mencadang tetapi tidak pernah menyelesaikan atau menggerakkan wang, dan setiap tanda yang dibangkitkan dijejak kembali ke dokumen, baris dan angka tepat asalnya — jejak audit penuh dibina dari hari pertama untuk semakan BNM/MAS/PIDM.",
  agentLoop: [
    "himpunan tuntutan, perlindungan polisi & sejarah tuntutan",
    "pecah & jalankan pekerja — perlindungan, jumlah, penipuan (alat MCP)",
    "selaraskan penemuan pekerja sebelum memaparkan",
  ],
  integrations: [
    "Sistem pentadbiran polisi / pengurusan tuntutan",
    "Simpanan dokumen atau peti masuk tuntutan",
    "Pilihan: rujukan kadar bengkel / panel perubatan, semakan anti-penipuan / tuntutan terdahulu",
  ],
  modelChoice: {
    approach: "Orkestrator tunggal + pekerja berperingkat",
    rationale:
      "Tuntutan benar-benar terbahagi kepada semakan bebas — perlindungan, jumlah, ketekalan, penipuan — jadi orkestrator yang mengagih kepada pekerja sesuai, tetapi pekerja ialah panggilan model dalam satu proses yang berkongsi konteks tuntutan, bukan ejen autonomi berasingan — boleh dipercayai, boleh diaudit, dan jauh lebih murah.",
    costNote:
      "Kami mengekalkan pekerja sebagai panggilan dalam-proses berperingkat (Haiku untuk semakan mudah, Sonnet untuk menyelaras, Opus hanya untuk adilan sukar) dan bukan armada berbilang-ejen penuh (≈15× token) — disimpan untuk kerja benar-benar terbuka dan luas, yang saringan tuntutan bukan.",
  },
  evaluation: [
    "Semakan deterministik dahulu — had perlindungan dan sub-had, aritmetik resit, ketekalan tarikh dan pihak",
    "Pengesahan sendiri — orkestrator menyelaras dan menyemak semula tanda pekerjanya sebelum memaparkannya",
    "KPI perniagaan — masa penilai setiap tuntutan, kebocoran ditangkap, kadar terus-lepas, ketekalan, dan kadar tanda palsu",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur saringan manual semasa pada sampel — masa setiap tuntutan, sejauh mana konsisten dua penilai mencapai keputusan sama, berapa banyak kebocoran terlepas, dan berapa tuntutan bersih dilewatkan tanpa perlu. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan, bukan diandaikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia mencapai cadangan yang betul?",
        metrics: [
          {
            name: "Persetujuan dengan penilai kanan",
            measures: "Ringkasan, tanda dan tindakan disyorkan sepadan dengan pakar pada tuntutan terpencil",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah ia menjalankan semakan yang betul untuk tuntutan ini?",
        metrics: [
          {
            name: "Pekerja diperlukan diagihkan",
            measures: "Setiap semakan yang jenis tuntutan wajibkan — perlindungan, jumlah, ketekalan, penipuan — dijalankan",
            target: "100%",
          },
          {
            name: "Tiada pekerja membazir",
            measures: "Ia menjalankan semakan yang tuntutan perlukan, bukan yang sia-sia",
            target: "dipantau",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap semakan betul?",
        metrics: [
          {
            name: "Ketepatan ekstrak medan & jumlah",
            measures: "Adakah ia membaca setiap angka — jumlah, had, tarikh — dengan betul daripada dokumen?",
            target: "≥ 98%",
          },
          {
            name: "Recall kebocoran / penipuan (kadar tangkap)",
            measures: "Daripada lebihan bayaran dan tokokan sebenar dalam set emas, berapa yang ditandakan",
            target: "≥ 95%",
          },
          {
            name: "Kadar tanda palsu",
            measures: "Berapa kerap ia menandakan tuntutan bersih sebagai masalah",
            target: "≤ 10%",
          },
          {
            name: "Tiada angka halusinasi",
            measures: "Setiap nombor dalam ringkasan boleh dijejak ke dokumen sumber",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak menyentuh tuntutan langsung sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas tuntutan sejarah — termasuk kebocoran dan penipuan diketahui. Setiap perubahan diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas tuntutan sejarah sebenar anda — termasuk yang ditokok dan penipuan — dan membuktikan ia menangkap kebocoran tanpa menanda-berlebihan fail bersih sebelum ia menyentuh tuntutan langsung. Pelancaran satu jenis tuntutan yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Adakah pasukan tuntutan anda menyelaras fail secara manual?",
  ctaDescription:
    "Beritahu kami jenis tuntutan dan volum anda, dan kami akan beritahu dengan jujur sama ada ejen tuntutan insurans berbaloi dibina — dan apa yang boleh dipulihkan.",
  faqs: [
    {
      question: "Adakah ejen meluluskan atau membayar tuntutan?",
      answer:
        "Tidak. Ia membaca, menyelaras dan menandakan, dan mencadangkan tindakan; penilai manusia membuat keputusan selesai, soal atau siasat. Ia tidak pernah menggerakkan wang. Manusia-dalam-gelung penting untuk insurans dan takaful terkawal di rantau ini.",
    },
    {
      question: "Bagaimana ia menangkap penipuan dan kebocoran?",
      answer:
        "Pekerja isyarat-penipuan mencari item baris pendua, tokokan melebihi kadar panel/bengkel, dokumen disunting, ketaktekalan garis masa dan pertindihan dengan tuntutan terdahulu — setiap satu didedahkan dengan dokumen dan angka tepat. Ia menanda untuk manusia siasat; ia tidak menuduh.",
    },
    {
      question: "Bagaimana ia mematuhi BNM/MAS, PIDM dan PDPA/PDPC?",
      answer:
        "Dengan jejak audit penuh supaya setiap cadangan boleh dibina semula, dan dibina mengikut keperluan PDPA/PDPC tentang di mana data tuntutan dan perubatan diproses, disimpan dan diakses. Anggap ini sebagai titik permulaan untuk pasukan pematuhan anda, bukan nasihat undang-undang.",
    },
    {
      question: "Berapa lama untuk dilaksanakan?",
      answer:
        "Bukti-konsep untuk satu jenis tuntutan yang fokus biasanya beberapa minggu; pelancaran penuh mengambil masa lebih lama, kebanyakannya kerana integrasi dan membiasakan penilai, bukan AI.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "保险与伊斯兰保险",
  title: "保险理赔智能体",
  cardSummary: "将理赔与保单、报告和收据逐一比对——并标记出对不上的地方。",
  heroSubhead:
    "我们设计、构建并部署一个 AI 智能体，端到端地读取一份理赔——保单、事故报告和每一张收据——将它们相互核对，并为你的理赔员提供一份可直接决策的档案，其中的漏损与欺诈信号已被标出——而赔付决定仍由人来做。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "它每拦下一笔多付、每快速放行一份干净理赔都是钱——理赔漏损通常占已赔付理赔的 5–10%，而一致的审查正是 BNM 操守审查所看重的。",
    complexityLabel: "高",
    complexityWhy:
      "编排器将理赔拆解为并行检查、对报告与收据的视觉/OCR、保单条款推理、欺诈信号，以及 BNM/MAS/PIDM/PDPA/PDPC 级的管控与审计轨迹。",
    timeToLive: "约数周（单一理赔类型）",
    bestFit: "对文件繁重的理赔做初审的保险公司与伊斯兰保险运营商",
  },
  outcomes: [
    "不再赔付保单不承保的部分——漏损与多计费在赔付前拦截",
    "低风险理赔直通——干净档案数分钟内结案，而非数天",
    "每一份理赔都得到同样的审查——不再因理赔员而异",
    "欺诈信号尽早浮现，并附上每一项背后的确切文件与数字",
  ],
  impact: {
    manualLabel: "当前人工初审",
    manualValue: "约 30–45 分钟",
    agentLabel: "使用智能体",
    agentValue: "约 6–10 分钟",
    saved:
      "每份理赔约节省 25–35 分钟——每月 400 份理赔约合 165–230 小时，大约相当于一名全职理赔员。而在漏损方面：一个每月赔付 RM 200 万的组合，仅在 5–10% 的漏损率上拦下 2 个百分点，就是每月挽回约 RM 40,000——构建的回本期以周计，而非以年计。",
    note: "为估算；实际时间与挽回额取决于理赔类型、文件数量与你当前的漏损率。",
  },
  businessProblem:
    "区域内的保险公司或伊斯兰保险运营商将每一份车险、医疗或财产理赔作为一叠材料接收——理赔表、保单明细、事故或警方报告、修理厂或医院发票，以及照片。理赔员一天的大部分时间花在机械性工作上：核对理赔是否真的在承保范围内、金额是否与收据一致、有无虚增或重复——而不是做判断。这个过程缓慢，疲劳时容易出错，两名理赔员对同一份档案的审查程度也不同，而漏过去的虚增便成了公司默默承担的理赔漏损。",
  manualWorkflow: {
    steps: [
      "打开理赔档案及每份文件",
      "阅读保单明细，看清真正承保什么",
      "用肉眼阅读事故报告与收据",
      "相互交叉核对金额、日期与承保范围",
      "追查缺失的文件或重复的明细项",
      "之后才做出结案 / 质询 / 调查的决定",
    ],
    breaks:
      "瓶颈在于将理赔与保单及收据核对——而非判断。这恰恰是最容易疲劳、因人而异、并让虚增作为漏损溜过去的部分。",
  },
  agenticWorkflow: {
    narrative:
      "我们以 orchestrator-workers（编排器-工作者）方式构建：一个主模型读取理赔，将其拆解为这份理赔实际需要的检查，并把每一项交给专门的工作者——保单承保、收据金额、报告一致性、重复/欺诈信号——然后汇总成一份有证据支撑的摘要，无法厘清的则上报给人处理。",
    steps: [
      "读取理赔、保单、报告与收据",
      "拆解为这份理赔所需的检查",
      "并行运行工作者（承保 · 金额 · 欺诈）",
      "核对结果并带证据标记",
      "汇总成可直接决策的档案",
    ],
    inLabel: "一份理赔进入",
    outLabel: "一份可直接决策的理赔档案",
    loopAround: {
      index: 1,
      label: "编排器只启动这份理赔所需的工作者并收集其结果——即 orchestrator-workers 模式。",
    },
    humanStep: "理赔员做出结案 / 质询 / 调查的决定",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "在理赔档案中",
    inMessage:
      "车险理赔，保单承保自身车损至 RM 15,000。修理厂发票合计：RM 8,400——含前保险杠更换 RM 1,200（第 4 行）及前保险杠更换 RM 1,200（第 9 行）。事故报告日期：3 月 12 日。发票日期：3 月 8 日。",
    outLabel: "智能体标记出什么",
    outLines: [
      "理赔在自身车损承保范围内（RM 8,400 vs RM 15,000 上限）",
      "重复明细项：前保险杠更换被计费两次（第 4 行与第 9 行）",
      "时间线不一致：发票日期 3 月 8 日，早于 3 月 12 日事故 4 天",
    ],
    outResult:
      "标记 2 处问题：RM 1,200 重复收费 + 维修发票开具早于事故日期",
    note: "每个标记都引用确切的文件、行号与数字 · 由你的理赔员做赔付决定 · 每份理赔约 RM 0.80–1.60。",
  },
  workflowDetail: [
    {
      title: "读取理赔档案",
      detail:
        "智能体读取档案中的每份文件——理赔表、保单明细、事故或警方报告、修理厂或医院发票，以及照片——读取文字，并对扫描件和手机照片使用视觉能力提取内容。",
    },
    {
      title: "拆解理赔（编排器）",
      detail:
        "主模型弄清这份具体理赔需要哪些检查——车险自身车损理赔、医疗定点理赔与财产理赔所需的测试并不相同——并规划要运行的工作者。",
    },
    {
      title: "运行工作者",
      detail:
        "专门的工作者并行运行：保单承保工作者（此风险、项目与金额是否真的在承保、限额与子限额之内？）、金额工作者（收据合计是否正确、是否与理赔金额一致？）、一致性工作者（日期、当事方与报告是否吻合？）、以及欺诈信号工作者（重复、虚增、被篡改文件、与既往理赔重叠）。",
    },
    {
      title: "核对并标记",
      detail:
        "编排器汇总各工作者的结果、加以核对，并标出两三项要紧的——未承保项目、重复行、日期不符——每一项都附带具体数字及其来源文件。",
    },
    {
      title: "汇总成可直接决策的档案",
      detail:
        "它生成一页式摘要：理赔、通过的检查、建议动作（结案 / 质询 / 调查），以及每个标记及其证据——供理赔员据此行动。",
    },
    {
      title: "交给人处理",
      detail:
        "理赔员审阅被标记的项目并做出结案、质询或调查的决定。没有任何理赔会被自动赔付。",
    },
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的 orchestrator-workers 工作流——主模型在运行时拆解理赔并委派给工作者，每个工具通过进程内 MCP 服务器提供。",
    "上下文：保单明细、承保规则与理赔材料被载入窗口——文本加上对扫描报告与收据照片的视觉识别——保单实时数据（限额、子限额、除外责任）按每份理赔实时读取而非旧副本，较长的档案会被压缩，确保没有内容被静默丢弃。",
    "工具：只读查询（保单/承保读取器、用于既往理赔重叠的理赔历史读取器、收据/OCR 提取器、修理厂/定点费率参考）与任何写入分离——智能体只把一份结构化建议写入理赔队列，绝不写入赔付。",
    "防护机制：无自主资金流动——它只能建议、绝不结案；输出锁定模式；最小权限、对保单管理或支付系统无写入权限；低置信度或高价值理赔转入高级队列。",
    "不可信输入：每份文件——发票 PDF、报告文本、收据图片——都被视为不可信并可抵御提示注入，因此被篡改的发票无法左右建议；PII（身份证、医疗、车辆、银行信息）均按 PDPA/PDPC 处理。",
    "恢复：当某个工作者置信度低或缺少文件时，编排器会重跑该工作者或索取该件，然后上交给理赔员——它绝不臆测理算；每条路径都终于人。",
    "可观测性与审计：每次工具调用、工作者结果与最终建议都被追踪并留痕，理赔员的决定对应记录于档案——一条 BNM/MAS/PIDM 审查可还原的机器可读审计轨迹，而非截图。",
    "评估框架：一组含已知漏损与欺诈的历史理赔黄金集，配合确定性检查（承保限额、算术、日期）并对模糊的欺诈判断采用 LLM 作为评审，每次改动在接触实时理赔前都经过回归测试。",
  ],
  models: [
    {
      task: "读取并提取文件内容（含扫描件与照片）",
      model: "Claude Sonnet",
      why: "对杂乱的发票、报告与收据照片具备可靠的视觉与结构化提取能力。",
    },
    {
      task: "跨理赔编排与核对",
      model: "Claude Sonnet",
      why: "主力模型——中等成本下拆解理赔、运行工作者并核对结果。",
    },
    {
      task: "高频简单检查（重复、算术、日期合理性）",
      model: "Claude Haiku",
      why: "对无需完整推理的批量是/否工作者检查最便宜、最快。",
    },
    {
      task: "裁定模糊欺诈 / 临界承保",
      model: "Claude Opus",
      why: "仅在工作者对疑难判断标记低置信度时调用——占理赔的一小部分。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每份理赔",
        value: "约 RM 0.80–1.60",
        note: "输入文件 + 编排 + 并行工作者 + 输出摘要，按 Claude Sonnet 的 token 费率",
      },
      {
        label: "AI 用量——每月 400 份理赔",
        value: "约 RM 280–560 / 月",
        note: "使用 Batch API 处理非紧急的夜间初审任务约可减半",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "聚焦单一理赔类型的智能体约需数周工程；简短的需求梳理后我们给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比其省去的理赔员时间与挽回的漏损，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于文件大小、理赔类型与每月业务量。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  howWeBuild:
    "它通过连接的工具直接接入你的保单管理与理赔系统，并按每份理赔读取你的实时承保规则——限额、子限额与除外责任——因此它是对照保单当前的实际状态来核查，而非缓存副本。它可以建议、但绝不结案或动用资金，它提出的每一个标记都能追溯到其来源的确切文件、行号与数字——完整审计轨迹从第一天就内建，可供 BNM/MAS/PIDM 审查。",
  agentLoop: [
    "理赔材料、保单承保与理赔历史",
    "拆解并运行工作者——承保、金额、欺诈（MCP 工具）",
    "在呈现前核对各工作者的结果",
  ],
  integrations: [
    "保单管理 / 理赔管理系统",
    "文件库或理赔收件箱",
    "可选：修理厂 / 医疗定点费率参考、反欺诈 / 既往理赔查询",
  ],
  modelChoice: {
    approach: "单一编排器 + 分级工作者",
    rationale:
      "一份理赔确实可拆分为独立检查——承保、金额、一致性、欺诈——因此由编排器委派给工作者是合适的，但工作者是同一进程内、共享理赔上下文的模型调用，而非各自独立的自主智能体——可靠、可审计，且成本低得多。",
    costNote:
      "我们把工作者保持为分级的进程内调用（Haiku 做简单检查、Sonnet 做核对、Opus 仅用于疑难裁定），而非完整的多智能体舰队（约 15 倍 token）——那留给真正开放式、广度优先的工作，而理赔初审并非如此。",
  },
  evaluation: [
    "先做确定性检查——承保限额与子限额、收据算术、日期与当事方一致性",
    "自我验证——编排器在呈现前核对并复查各工作者的标记",
    "业务 KPI——每份理赔的理赔员耗时、拦下的漏损、直通率、一致性、以及误报率",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量当前的人工初审——每份理赔耗时、两名理赔员得出相同结论的一致程度、有多少漏损溜过、以及有多少干净理赔被无谓拖延。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的，而非假设。",
    dimensions: [
      {
        dim: "Goal",
        desc: "是否得出了正确的建议？",
        metrics: [
          {
            name: "与资深理赔员的一致度",
            measures: "在留出理赔上，其摘要、标记与建议动作是否与专家一致",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "是否为这份理赔运行了正确的检查？",
        metrics: [
          {
            name: "必需工作者已派发",
            measures: "该理赔类型规定的每一项检查——承保、金额、一致性、欺诈——都已运行",
            target: "100%",
          },
          {
            name: "无多余工作者",
            measures: "它运行理赔所需的检查，而非无谓的检查",
            target: "持续监控",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各项检查是否正确？",
        metrics: [
          {
            name: "字段与金额提取准确率",
            measures: "是否从文件中正确读取每个数字——合计、限额、日期？",
            target: "≥ 98%",
          },
          {
            name: "漏损 / 欺诈召回率（拦截率）",
            measures: "黄金集中真实的多付与虚增，有多少被标记出来",
            target: "≥ 95%",
          },
          {
            name: "误报率",
            measures: "多久会把一份干净理赔标记为问题",
            target: "≤ 10%",
          },
          {
            name: "无凭空捏造的数字",
            measures: "摘要中的每个数字都能追溯到来源文件",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于历史理赔黄金集（含已知漏损与欺诈）上超过人工基线并达到这些 GPA 目标之前，它不会接触任何实时理赔。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的历史理赔上做概念验证——包括虚增和欺诈的案例——并证明它能拦下漏损而不过度标记干净档案，然后再接触实时理赔。聚焦单一理赔类型的落地只需数周。",
  ctaTitle: "你的理赔团队还在用人工核对档案吗？",
  ctaDescription:
    "告诉我们你的理赔类型和业务量，我们会诚实告诉你保险理赔智能体是否值得构建，以及能挽回多少。",
  faqs: [
    {
      question: "智能体会批准或赔付理赔吗？",
      answer:
        "不会。它负责读取、核对和标记，并建议一个动作；由人类理赔员做出结案、质询或调查的决定。它绝不动用资金。在区域内受监管的保险与伊斯兰保险中，人在环路中至关重要。",
    },
    {
      question: "它如何发现欺诈与漏损？",
      answer:
        "欺诈信号工作者会查找重复明细项、超出定点/修理厂费率的虚增、被篡改的文件、时间线不一致以及与既往理赔的重叠——每一项都附上确切的文件与数字。它标记出来供人调查；它不做指控。",
    },
    {
      question: "如何符合 BNM/MAS、PIDM 与 PDPA/PDPC 的要求？",
      answer:
        "通过完整的审计轨迹让每一条建议都可被还原，并围绕 PDPA/PDPC 对理赔与医疗数据处理、存储与访问位置的要求来构建。请将此视为你合规团队的起点，而非法律意见。",
    },
    {
      question: "部署需要多长时间？",
      answer:
        "针对单一聚焦理赔类型的概念验证通常只需数周；全面落地需要更长时间，主要是因为集成和让理赔员适应，而不是 AI 本身。",
    },
  ],
};

export const insuranceClaims: AgentUseCase = {
  structure: {
    slug: "insurance-claims",
    status: "available",
    pattern: "orchestrator-workers",
    route: "solutions/agent-use-cases/insurance-claims",
    heroImage: "/images/agent-use-cases/insurance-claims-hero.jpg",
    cardImage: "/images/agent-use-cases/insurance-claims-card.jpg",
    relatedUseCases: ["credit-evaluation", "invoice-matching"],
    trackLocation: "agent_use_case_insurance_claims",
    valueScore: 4,
    complexityScore: 5,
  },
  content: { en, ms, zh },
};
